import clsx from 'clsx'
import { formatDistanceToNowStrict } from 'date-fns'
import { fr } from 'date-fns/locale'
import { type ReactElement, useMemo } from 'react'
import {
  type OrderCollectionReponse,
  orderFinalStatusDetail,
  orderStatus
} from '~/api/queries/order'
import { usePathViewedActivity } from '~/api/queries/pathViewed'
import { Badge } from '~/components/Global/Blocks/Badge/Badge'
import { Card } from '~/components/Global/Blocks/Card/Card'
import { MetaItem } from '~/components/Global/Blocks/Card/MetaItem'
import { NotificationIndicator } from '~/components/Global/Elements/NotificationIndicator/NotificationIndicator'
import { profilesListStandardHeight } from '~/components/Global/Elements/ProfilesList/ProfilesList.css'
import { Shimmer } from '~/components/Global/Elements/Shimmer/Shimmer'
import { Tooltip } from '~/components/Global/Elements/Tooltip/Tooltip'
import { useTaxonomy } from '~/domains/taxonomies/queries/useTaxonomy'
import { UsersList } from '~/domains/users/components/UsersList'
import { vars } from '~/styles/theme.css'
import { invertColor } from '~/utils'
import { formatCurrency } from '~/utils/currency'
import { formatDateTimeShort } from '~/utils/date'
import { buildV3Url } from '~/utils/legacy'
import {
  figure,
  orderActivityIndicator,
  pendingApplicationsFigure,
  summaryArchived
} from './OrderCard.css'

export type OrderCardProps = {
  order: OrderCollectionReponse
}

export const OrderCard = ({ order }: OrderCardProps): ReactElement => {
  const { data: orderStatuses } = useTaxonomy('applicant_order_statuses')
  const { hasNewActivity } = usePathViewedActivity(`/orders/${order.id}`)

  const orderStatusesCount = orderStatuses?.map((orderStatus) => {
    const statusCount = order.applicantCountByStatus.find(
      (status) => status.id === orderStatus.id
    )
    return {
      number: statusCount ? statusCount.applicantCount : 0,
      statusName: orderStatus.name,
      color: orderStatus.color
    }
  })

  const orderFinalStatusDetailName = useMemo(() => {
    return orderFinalStatusDetail?.find(
      (status) => status.id === order.statusDetail
    )?.name
  }, [order.statusDetail])

  const isOpen = order.status === 1
  const location = order.matchingLocation
    ? `${order.matchingLocation.postcode ?? ''} ${order.matchingLocation.city ?? ''}`.trim()
    : ''

  const metas = [
    <MetaItem key="location" title={location ? 'Lieu de mission' : ''}>
      <span title={location}>{location}</span>
    </MetaItem>,
    <MetaItem key="salary" title={order.contractTerm?.name}>
      {order.salaryMin
        ? `${formatCurrency(order.salaryMin, order.salaryCurrency)} ${order.salaryMax ? `- ${formatCurrency(order.salaryMax, order.salaryCurrency)}` : ''}`
        : ''}
    </MetaItem>,
    <MetaItem
      key="created"
      title={`${formatDistanceToNowStrict(new Date(order.created), { addSuffix: true, locale: fr })}`}
      tooltip={`Créée le ${formatDateTimeShort(new Date(order.created))}`}
    >
      {order?.status === 1 && (
        <Badge title="Ouverte" backgroundColor={vars.color.success} />
      )}
      {order?.status !== 1 && (
        <Badge
          title={`${orderStatus[order?.status]?.name} ${orderFinalStatusDetailName ? `(${orderFinalStatusDetailName})` : ''}`}
          backgroundColor={vars.color.danger}
        />
      )}
    </MetaItem>,
    <MetaItem key="users" align="right">
      <UsersList
        users={order.users}
        className={profilesListStandardHeight}
        key="users"
      />
    </MetaItem>
  ]

  return (
    <Card
      href={buildV3Url(`/orders/${order.id}`)}
      disabled={!isOpen}
      classNames={[!isOpen ? summaryArchived : '']}
      title={order.company.name}
      subtitle={
        (order.jobTitle ?? '') +
        (order.qualifications.length > 0
          ? ` (${order.qualifications.map((q) => q.name).join(', ')})`
          : '')
      }
      metaData={metas}
    >
      {hasNewActivity && (
        <NotificationIndicator
          tooltip="Nouvelle activité"
          className={orderActivityIndicator}
        />
      )}
      <Tooltip
        text={`Candidature${(order.pendingApplicationsCount ?? 0) >= 2 ? 's' : ''} à traiter`}
        key="pendingApplications"
        asChild
        direction="top"
      >
        <span className={clsx(figure, pendingApplicationsFigure)}>
          {order.pendingApplicationsCount ?? 0}
        </span>
      </Tooltip>
      {orderStatusesCount?.map(({ number, statusName, color }) => (
        <Tooltip text={statusName} key={statusName} as="span" direction="top">
          <span
            style={{
              background: color,
              color: invertColor(color, vars.color.dark, vars.color.white)
            }}
            className={figure}
          >
            {number}
          </span>
        </Tooltip>
      ))}
      {(!orderStatusesCount || orderStatusesCount.length === 0) &&
        [...Array(5)].map((_, index) => (
          <Shimmer
            height={20}
            width={20}
            key={`status-${
              // biome-ignore lint/suspicious/noArrayIndexKey: Shimmer cant have unique key
              index
            }`}
          />
        ))}
    </Card>
  )
}
